import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Button,
  Tabs,
  Input,
  List,
  message,
  Tooltip,
} from 'antd';
import { FileOutlined, ClockCircleOutlined } from '@ant-design/icons';
import TaskTable from '../Tasks/TaskTable';
import ListView from '../Vacancies/ListView';
import CreateModal from '../Tasks/CreateModal';
import StatusTag from '../StatusTag';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCandidateHistory } from '../../redux/candidatesSlice';
import { createTask } from '../../redux/tasksSlice';
import { fetchTasksByCandidate, clearCandidateTasks } from '../../redux/tasksSlice';
import { STATUSES } from '../../constants/candidateColumnLabels';
import { updateVacancyCandidateStatus, vacancyCandidateReturnToWork } from '../../redux/vacancyCandidatesSlice';
import { revertCandidateStatus, updateCandidateStatusOptimistic } from '../../redux/candidatesSlice';
import { fetchCandidateById } from '../../redux/candidatesSlice';
import CompletionModal from './CompletionModal';
import {
  canChangeCandidateStatus,
  canReturnCandidateToWork,
  canCreateTaskForCandidate,
  canCommentOnCandidate
} from '../../utils/candidatePermissions';

const { TabPane } = Tabs;
const { TextArea } = Input;

const CustomComment = ({ item, users }) => {
  if (!item) return null;

  const user = users?.find(u => u.id === item.created_by);
  const authorName = user ? `${user.first_name} ${user.last_name}` : 'Неизвестный пользователь';

  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ fontWeight: 'bold' }}>{authorName}</div>
      <div style={{ margin: '4px 0' }}>{item.comment}</div>
      <div style={{ fontSize: 12, color: '#999' }}>
        {new Date(item.created_at).toLocaleString()}
      </div>
    </div>
  );
};

const CandidateViewModal = ({
  visible,
  onClose,
  onCommentSubmit,
  candidate,
  users,
  getFiles,
}) => {
  const { orgId, role: roleFromStore } = useSelector((state) => state.auth);
  const { history, historyLoading } = useSelector((state) => state.candidates);
  const { candidateTasks } = useSelector((state) => state.tasks);
  const { funnelStatuses, loading: funnelStatusesLoading } = useSelector((state) => state.vacancies);
  const dispatch = useDispatch();
  const { organisations } = useSelector((state) => state.organisationDepartment);

  const [comment, setComment] = useState('');
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [createTaskModalInitialValues, setCreateTaskModalInitialValues] = useState(null);
  const [completionModalVisible, setCompletionModalVisible] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [fieldHistoryModalVisible, setFieldHistoryModalVisible] = useState(false);
  const [selectedFieldHistory, setSelectedFieldHistory] = useState(null);
  const [returnToWorkModalVisible, setReturnToWorkModalVisible] = useState(false);
  const [returnToWorkComment, setReturnToWorkComment] = useState('');
  const [pendingReturnData, setPendingReturnData] = useState(null);

  const storedRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const effectiveRole = roleFromStore || storedRole;
  const canChangeStatus = canChangeCandidateStatus(effectiveRole);
  const canReturnToWork = canReturnCandidateToWork(effectiveRole);
  const canCreateTask = canCreateTaskForCandidate(effectiveRole);
  const canComment = canCommentOnCandidate(effectiveRole);

  const candidateFiles = useMemo(() => candidate?.files || [], [candidate?.files]);

  const fetchTasks = useCallback(async () => {
    if (!candidate?.id) return;

    try {
      await dispatch(fetchTasksByCandidate({ candidateId: candidate.id })).unwrap();
    } catch (error) {
      console.error('Ошибка при получении задач:', error);
      message.error('Ошибка при загрузке задач кандидата');
    }
  }, [candidate, dispatch]);


  const fetchHistory = useCallback(async (orgId) => {
    if (!candidate?.id) return;

    try {
      await dispatch(fetchCandidateHistory({ id: candidate.id, organisationId: orgId })).unwrap();
    } catch (error) {
      message.error('Ошибка при загрузке истории кандидата');
      console.error(error);
    }
  }, [candidate, dispatch]);

  useEffect(() => {
    if (!candidate?.id || !visible) return;

    const loadData = async () => {
      if (orgId) {
        await Promise.all([
          fetchTasks(),
          fetchHistory(orgId)
        ]);
      }
    };

    loadData();
  }, [candidate, visible, orgId, fetchTasks, fetchHistory]);

  useEffect(() => {
    if (!visible) {
      dispatch(clearCandidateTasks());
    }
  }, [visible, dispatch]);


  const getStatusName = useCallback((statusId) => {
    if (!funnelStatuses || funnelStatuses.length === 0) {
      return `Статус ${statusId}`;
    }

    const status = funnelStatuses.find(s => s.id === parseInt(statusId));
    return status ? status.name : `Статус ${statusId}`;
  }, [funnelStatuses]);

  const getStatusKeyFromName = (statusName) => {
    if (statusName.includes('Новый')) return 'new';
    if (statusName.includes('Перезвонить')) return 'call_back';
    if (statusName.includes('Собеседование')) return 'interview';
    if (statusName.includes('Сбор рекомендаций')) return 'references';
    if (statusName.includes('Предложение о работе')) return 'offer';
    if (statusName.includes('Оформление документов')) return 'documentation';
    if (statusName.includes('Завершенный') || statusName.includes('Завершить')) return 'hired';
    if (statusName.includes('Отклонён') || statusName.includes('Отказ')) return 'failed';
    return null;
  };

  const isCompletionStatus = (statusName) => {
    const statusKey = getStatusKeyFromName(statusName);
    return statusKey === 'hired' || statusKey === 'failed';
  };

  const getFailureReasonLabel = (failureReason) => {
    const failureReasonLabels = {
      'no_show': 'Не пришел на собеседование',
      'irrelevant_experience': 'Нерелевантный опыт работы',
      'irrelevant_salary': 'Нерелевантный доход',
      'no_contact': 'Не выходит на связь',
      'not_interested_product': 'Не интересен товар',
      'unsuitable_schedule': 'Не подходит график работы',
      'not_actively_searching': 'Не актуален поиск работы',
      'not_interested_functionality': 'Не интересен функционал',
      'long_commute': 'Далеко добираться',
      'failed_test_assignment': 'Не прошел тестовое задание',
      'vacancy_closed': 'Вакансия закрыта',
      'other_requirements': 'Не подходит по другим требованиям'
    };
    return failureReasonLabels[failureReason] || failureReason;
  };

  const getStatusTag = useCallback((status) => {
    if (!status) return <Tag color="default">Не указан</Tag>;

    const statusMap = {
      new: { color: 'blue', text: 'Новый' },
      call_back: { color: 'orange', text: 'Перезвонить' },
      interview: { color: 'orange', text: 'Собеседование' },
      references: { color: 'orange', text: 'Сбор рекомендаций' },
      offer: { color: 'orange', text: 'Предложение о работе' },
      documentation: { color: 'orange', text: 'Оформление документов' },
      hired: { color: 'green', text: 'Трудоустройство' },
      failed: { color: 'red', text: 'Отклонен' },
    };
    return <Tag color={statusMap[status]?.color}>{statusMap[status]?.text}</Tag>;
  }, []);

  const handleCommentSubmit = useCallback(async () => {
    if (!canComment) {
      message.error('Недостаточно прав для добавления комментариев');
      return;
    }
    if (!comment.trim()) {
      message.warning('Введите комментарий перед отправкой');
      return;
    }
    if (!candidate?.id) {
      message.error('Кандидат не выбран');
      return;
    }
    try {
      if (onCommentSubmit) {
        await onCommentSubmit(candidate.id, comment);
      }
      setComment('');
      await dispatch(fetchCandidateHistory({ id: candidate.id, organisationId: orgId })).unwrap();
    } catch (error) {
      message.error('Ошибка при отправке комментария');
      console.error(error);
    }
  }, [comment, candidate, orgId, onCommentSubmit, dispatch, canComment]);

  const handleCreateTask = useCallback(() => {
    if (!canCreateTask) {
      message.error('Недостаточно прав для создания задачи');
      return;
    }
    if (!candidate?.id) {
      message.error('Кандидат не выбран');
      return;
    }

    setCreateTaskModalInitialValues({
      candidate_id: candidate.id,
      organisation_id: orgId
    });
    setIsCreateTaskModalOpen(true);
  }, [candidate, orgId, canCreateTask]);

  const handleSubmitTask = useCallback(async (taskData) => {
    if (!canCreateTask) {
      message.error('Недостаточно прав для создания задачи');
      return;
    }
    try {
      await dispatch(createTask({ task: taskData })).unwrap();
      setIsCreateTaskModalOpen(false);
      fetchTasks();
    } catch (error) {
      message.error('Ошибка при создании задачи');
    }
  }, [dispatch, fetchTasks, canCreateTask]);

  const handleCompletionConfirm = async (completionData) => {
    if (!pendingStatusChange) return;

    const { candidateId, vacancyId, originalStatus, newStatus } = pendingStatusChange;

    try {
      dispatch(updateCandidateStatusOptimistic({
        vacancyId: vacancyId,
        candidateId: candidateId,
        statusId: newStatus
      }));

      await dispatch(updateVacancyCandidateStatus({
        vacancyId: vacancyId,
        candidateId: candidateId,
        statusId: newStatus,
        completionData: completionData
      })).unwrap();

      await dispatch(fetchCandidateById({ id: candidateId, organisationId: orgId })).unwrap();

      setCompletionModalVisible(false);
      setPendingStatusChange(null);
      message.success('Статус кандидата обновлен');

    } catch (error) {
      console.error('Error updating candidate status:', error);
      dispatch(revertCandidateStatus({
        vacancyId: vacancyId,
        candidateId: candidateId,
        originalStatusId: originalStatus
      }));
      message.error('Ошибка при обновлении статуса кандидата');
    }
  };

  const handleCompletionCancel = () => {
    setCompletionModalVisible(false);
    setPendingStatusChange(null);
  };

  const handleReturnToWork = useCallback((vacancyId) => {
    if (!canReturnToWork) {
      message.error('Недостаточно прав для изменения статуса');
      return;
    }
    setPendingReturnData({ vacancyId, candidateId: candidate.id });
    setReturnToWorkModalVisible(true);
  }, [candidate, canReturnToWork]);

  const handleReturnToWorkConfirm = async () => {
    if (!returnToWorkComment.trim()) {
      message.error('Комментарий обязателен для возврата в работу');
      return;
    }

    try {
      await dispatch(vacancyCandidateReturnToWork({
        vacancyId: pendingReturnData.vacancyId,
        candidateId: pendingReturnData.candidateId,
        comment: returnToWorkComment
      })).unwrap();

      message.success('Кандидат успешно возвращен в работу');

      await dispatch(fetchCandidateById({ id: candidate.id, organisationId: orgId })).unwrap();

      if (orgId) {
        await fetchHistory(orgId);
      }

      setReturnToWorkModalVisible(false);
      setReturnToWorkComment('');
      setPendingReturnData(null);
    } catch (error) {
      message.error('Ошибка при возврате кандидата в работу');
      console.error(error);
    }
  };

  const handleReturnToWorkCancel = () => {
    setReturnToWorkModalVisible(false);
    setReturnToWorkComment('');
    setPendingReturnData(null);
  };

  const handleFieldHistoryClick = useCallback((fieldName) => {
    if (!history || !history[fieldName]) {
      message.info('Нет истории изменений для этого поля');
      return;
    }

    const fieldHistory = [...history[fieldName]].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    setSelectedFieldHistory({
      fieldName,
      history: fieldHistory
    });
    setFieldHistoryModalVisible(true);
  }, [history]);

  const getFieldDisplayName = (fieldName) => {
    const fieldNames = {
      'phone_number': 'Телефон',
      'full_name': 'ФИО',
      'status': 'Статус',
      'email': 'Email',
      'salary_range': 'Желаемый уровень дохода',
      'files': 'Файлы'
    };
    return fieldNames[fieldName] || fieldName;
  };

  const renderHistoryItem = useCallback((item, type = 'general') => {
    if (!item) return null;
    const user = users?.find(u => u.id === item.created_by);
    const authorName = user ? `${user.first_name} ${user.last_name}` : 'Неизвестный пользователь';

    const renderStatusValue = (value) => {
      const key = getStatusKeyFromName(String(value || ''));
      if (key && STATUSES[key]) {
        return <StatusTag status={key} statuses={STATUSES} />;
      }
      if (!value) {
        return <Tag color="default">Не указан</Tag>;
      }
      return <Tag color="default">{value}</Tag>;
    };

    const getFieldDisplayName = (fieldName) => {
      const fieldNames = {
        'phone_number': 'Телефон',
        'full_name': 'ФИО',
        'status': 'Статус',
        'email': 'Email',
        'salary_range': 'Желаемый уровень дохода'
      };
      return fieldNames[fieldName] || fieldName;
    };

    // removed: getStatusDisplayName (replaced with renderStatusValue)

    return (
      <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
        {type === 'comment' ? (
          <>
            <div>
              <strong>{authorName}</strong> оставил комментарий
            </div>
            <div style={{ marginTop: 4, fontStyle: 'italic' }}>
              {item.comment}
            </div>
          </>
        ) : type === 'field_change' ? (
          <div>
            <strong>{authorName}</strong> изменил {getFieldDisplayName(item.field_name)}:
            <span style={{ marginLeft: 4 }}>
              {item.field_name === 'status' ? (
                item.is_initial_setup ? (
                  <>
                    <span style={{ color: '#999' }}>Установлено значение: </span>
                    <span style={{ fontWeight: 'normal' }}>{renderStatusValue(item.current_value)}</span>
                  </>
                ) : (
                  <>
                    <span style={{ color: '#999' }}>Было: </span>
                    {renderStatusValue(item.previous_value)}
                    <span style={{ margin: '0 8px' }}>→</span>
                    <span style={{ fontWeight: 'normal' }}>{renderStatusValue(item.current_value)}</span>
                  </>
                )
              ) : (
                item.is_initial_setup ? (
                  <span style={{ color: '#999' }}>Установлено значение: </span>
                ) : (
                  <>
                    <span style={{ color: '#999' }}>Было: </span>
                    {item.previous_value ? `"${item.previous_value}"` : 'Не указано'}
                    <span style={{ margin: '0 8px' }}>→</span>
                  </>
                )
              )}
              {item.field_name !== 'status' && (
                <span style={{ fontWeight: 'normal' }}>
                  {item.current_value ? `"${item.current_value}"` : 'Не указано'}
                </span>
              )}
            </span>
          </div>
        ) : type === 'vacancy_status_change' ? (
          <div>
            <strong>{authorName}</strong> изменил статус по вакансии "{item.vacancy_name}":
            <span style={{ marginLeft: 4 }}>
              {renderStatusValue(item.previous_value)} → {renderStatusValue(item.current_value)}
            </span>
          </div>
        ) : type === 'file_change' ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                color: item.comment?.includes('Удален') ? '#ff4d4f' :
                  item.comment?.includes('Добавлен') ? '#52c41a' : '#1890ff'
              }}>
                <strong>{authorName}</strong> {item.comment || 'Действие с файлом'}
              </span>
            </div>
          </div>
        ) : (
          <div>
            <strong>{authorName}</strong> изменил статус: {getStatusTag(item.previous_status)} → {getStatusTag(item.new_status)}
          </div>
        )}
        <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
          {new Date(item.created_at).toLocaleString()}
        </div>
      </div>
    );
  }, [users, getStatusTag]);

  const renderFilesSection = useCallback(() => {
    if (!candidateFiles || candidateFiles.length === 0) {
      return <p>Нет прикрепленных файлов</p>;
    }

    const allFileHistory = history?.files || [];

    return (
      <div>
        <List
          dataSource={candidateFiles}
          renderItem={file => {
            const fileHistory = allFileHistory.filter(h => h.file_id === file.id) || [];
            const hasFileHistory = fileHistory.length > 0;

            return (
              <List.Item>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Button
                    type="link"
                    icon={<FileOutlined />}
                    size="small"
                    onClick={() => window.open('https://dev.1ak-group.com/service/static/' + file.path, '_blank')}
                  >
                    {file.name || 'Файл'}
                  </Button>
                  {hasFileHistory && (
                    <Tooltip title="История загрузки файла">
                      <Button
                        type="text"
                        size="small"
                        icon={<ClockCircleOutlined />}
                        onClick={() => {
                          setSelectedFieldHistory({
                            fieldName: 'files',
                            history: fileHistory,
                            fileName: file.name || 'Файл'
                          });
                          setFieldHistoryModalVisible(true);
                        }}
                        style={{ padding: '4px 8px', minWidth: 'auto' }}
                      />
                    </Tooltip>
                  )}
                </div>
              </List.Item>
            );
          }}
        />

      </div>
    );
  }, [candidateFiles, history]);

  const renderVacanciesAndStatuses = useCallback(() => {
    if (!candidate) return <span>Нет данных о кандидате</span>;

    if (candidate.vacancies && Array.isArray(candidate.vacancies) && candidate.vacancies.length > 0) {
      if (funnelStatusesLoading) {
        return <span>Загрузка...</span>;
      }

      if (!funnelStatuses || funnelStatuses.length === 0) {
        return <span>Статусы не загружены</span>;
      }

      const vacancyItems = candidate.vacancies.map(vacancy => {
        const vacancyName = vacancy.job_title || 'Не указана';
        const statusName = getStatusName(vacancy.status_id);
        const statusKey = getStatusKeyFromName(statusName);

        const resultCandidate = vacancy.result_candidate;
        const failureReason = vacancy.failure_reason;
        const completionWork = vacancy.completion_work;

        return {
          vacancyName,
          statusName,
          statusKey,
          resultCandidate,
          failureReason,
          completionWork
        };
      });

      const visibleItems = vacancyItems.slice(0, 5);
      const hasMore = vacancyItems.length > 5;

      return (
        <div>
          {visibleItems.map((item, index) => (
            <div key={index} style={{ marginBottom: 12, padding: '8px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                {item.vacancyName}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: '12px', color: '#666' }}>Статус:</span>
                {item.statusKey && STATUSES[item.statusKey] ? (
                  <StatusTag status={item.statusKey} statuses={STATUSES} />
                ) : (
                  <Tag color="default">{item.statusName}</Tag>
                )}
              </div>

              {item.resultCandidate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: '12px', color: '#666' }}>Результат:</span>
                  {item.resultCandidate === 'success' && (
                    <Tag color="green">Успех</Tag>
                  )}
                  {item.resultCandidate === 'failure' && (
                    <Tag color="red">Провал</Tag>
                  )}
                  {item.resultCandidate === 'reserved' && (
                    <Tag color="blue">В резерве</Tag>
                  )}
                </div>
              )}

              {item.failureReason && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: '12px', color: '#666' }}>Причина провала:</span>
                  <Tag color="red">{getFailureReasonLabel(item.failureReason)}</Tag>
                </div>
              )}

              {item.completionWork && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '12px', color: '#666' }}>Дата завершения:</span>
                  <Tag color="green">{new Date(item.completionWork).toLocaleDateString()}</Tag>
                </div>
              )}
            </div>
          ))}
          {hasMore && (
            <div style={{ color: '#999', fontStyle: 'italic' }}>
              ... и еще {vacancyItems.length - 5} вакансий
            </div>
          )}
        </div>
      );
    }

    return <span>Нет данных о вакансиях</span>;
  }, [candidate, funnelStatuses, funnelStatusesLoading, getStatusName]);

  const handleStatusChange = async (item, newStatus, newIndex) => {
    if (!canChangeStatus) {
      message.error('Недостаточно прав для изменения статуса');
      return;
    }
    let candidateId, vacancyId, originalStatus;

    if (item.candidate && item.vacancyInfo) {
      candidateId = item.candidate.id;
      vacancyId = item.vacancyInfo.vacancy_id || item.vacancyInfo.id;
      originalStatus = item.candidate.status;
    } else {
      candidateId = item.id;
      vacancyId = item.vacancy_id;
      originalStatus = item.status;
    }

    const newStatusName = funnelStatuses?.find(s => s.id === parseInt(newStatus))?.name;

    if (newStatusName && isCompletionStatus(newStatusName)) {
      setPendingStatusChange({
        candidateId,
        vacancyId,
        originalStatus,
        newStatus: parseInt(newStatus),
        newStatusName
      });
      setCompletionModalVisible(true);
      return;
    }

    try {
      dispatch(updateCandidateStatusOptimistic({ vacancyId: vacancyId, candidateId: candidateId, statusId: parseInt(newStatus) }));

      await dispatch(updateVacancyCandidateStatus({
        vacancyId: vacancyId,
        candidateId: candidateId,
        statusId: parseInt(newStatus)
      })).unwrap();

      await dispatch(fetchCandidateById({ id: candidateId, organisationId: orgId })).unwrap();

    } catch (error) {
      console.error('Error updating candidate status:', error);
      dispatch(revertCandidateStatus({ vacancyId: vacancyId, candidateId: candidateId, originalStatusId: originalStatus }));
      message.error('Ошибка при обновлении статуса кандидата');
    }
  };

  const comments = useMemo(() => {
    const allComments = [];
    if (Array.isArray(history?.comments)) {
      allComments.push(...history.comments.filter(item => item.comment));
    }

    if (Array.isArray(history?.vacancies)) {
      history.vacancies.forEach(vacancy => {
        if (Array.isArray(vacancy.history?.comments)) {
          allComments.push(...vacancy.history.comments.filter(item => item.comment));
        }
      });
    }

    return allComments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [history]);

  const allHistoryItems = useMemo(() => {
    if (!history || !candidate) return [];

    const items = [];

    Object.keys(history).forEach(key => {
      if (key !== 'comments' && key !== 'vacancies' && key !== 'files' && Array.isArray(history[key])) {
        const fieldHistory = [...history[key]].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        fieldHistory.forEach((item, index) => {
          let currentValue;
          if (index === fieldHistory.length - 1) {
            currentValue = candidate?.[key];
          } else {
            currentValue = fieldHistory[index + 1].previous_value;
          }

          items.push({
            ...item,
            type: 'field_change',
            field_name: key,
            current_value: currentValue,
            is_initial_setup: item.previous_value === null || item.previous_value === 'None'
          });
        });
      }
    });

    if (Array.isArray(history.vacancies)) {
      history.vacancies.forEach(vacancy => {
        if (vacancy.history && Array.isArray(vacancy.history.status)) {
          const candidateVacancy = candidate?.vacancies?.find(v => v.id === vacancy.vacancy_id);
          const vacancyName = candidateVacancy?.job_title || `Вакансия #${vacancy.vacancy_id}`;

          const statusHistory = [...vacancy.history.status].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

          statusHistory.forEach((item, index) => {
            let currentValue;
            if (index === statusHistory.length - 1) {
              const currentStatusId = candidateVacancy?.status_id;
              currentValue = getStatusName(currentStatusId);
            } else {
              currentValue = statusHistory[index + 1].previous_value;
            }

            items.push({
              ...item,
              type: 'vacancy_status_change',
              vacancy_id: vacancy.vacancy_id,
              vacancy_name: vacancyName,
              current_value: currentValue
            });
          });
        }
      });
    }

    if (Array.isArray(history.files)) {
      history.files.forEach(fileItem => {
        items.push({
          ...fileItem,
          type: 'file_change'
        });
      });
    }

    return items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [history, candidate, getStatusName]);

  const renderGeneralInfo = useCallback(() => {
    if (!candidate) return null;

    const renderFieldWithHistory = (fieldName, value, label) => {
      const hasHistory = history && history[fieldName] && history[fieldName].length > 0;

      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span>{value}</span>
          {hasHistory && (
            <Tooltip title="История изменений">
              <Button
                type="text"
                size="small"
                icon={<ClockCircleOutlined />}
                onClick={() => handleFieldHistoryClick(fieldName)}
                style={{ padding: '4px 8px', minWidth: 'auto' }}
              />
            </Tooltip>
          )}
        </div>
      );
    };

    return (
      <div className="candidate-general-info">
        <Descriptions bordered column={1}>
          <Descriptions.Item label="ID">{candidate.id}</Descriptions.Item>
          <Descriptions.Item label="ФИО">
            {renderFieldWithHistory('full_name', candidate.full_name, 'ФИО')}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {renderFieldWithHistory('email', candidate.email || 'Не указан', 'Email')}
          </Descriptions.Item>
          <Descriptions.Item label="Телефон">
            {renderFieldWithHistory('phone_number', candidate.phone_number || 'Не указан', 'Телефон')}
          </Descriptions.Item>
          <Descriptions.Item label="Желаемый уровень дохода">
            {renderFieldWithHistory(
              'salary_range',
              candidate.salary_range ? `${candidate.salary_range}` : 'Не указан',
              'Желаемый уровень дохода'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Вакансии и статусы">{renderVacanciesAndStatuses()}</Descriptions.Item>
          <Descriptions.Item label="Резюме">
            {renderFilesSection()}
          </Descriptions.Item>
          <Descriptions.Item label="Дата создания">
            {new Date(candidate.created_at).toLocaleDateString()}
          </Descriptions.Item>
        </Descriptions>
        <style>{`
        .candidate-general-info .ant-descriptions-view table { table-layout: fixed; width: 100%; }
        .candidate-general-info .ant-descriptions-item-label { width: 33.333%; }
        .candidate-general-info .ant-descriptions-item-content { width: 66.667%; }
      `}</style>
      </div>
    );
  }, [candidate, renderVacanciesAndStatuses, renderFilesSection, history, handleFieldHistoryClick]);

  return (
    <Modal
      title="Просмотр кандидата"
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          Закрыть
        </Button>,
      ]}
      width={1000}
      destroyOnClose
    >
      {candidate && (
        <>
          <Tabs defaultActiveKey="general">
            <TabPane tab="Общая информация" key="general">
              {renderGeneralInfo()}
            </TabPane>

            <TabPane tab="Вакансии кандидата" key="vacancies">
              {candidate?.vacancies && candidate.vacancies.length > 0 ? (
                <div>
                  {(() => {
                    const candidateVacancies = candidate.vacancies;
                    const activeVacancies = candidateVacancies.filter(v => v.status !== 'closed' && v.status !== 'hanging');
                    const archivedVacancies = candidateVacancies.filter(v => v.status === 'closed' || v.status === 'hanging');

                    { console.log("candidateVacancies", activeVacancies) }
                    return (
                      <Tabs size="small" tabPosition="top">
                        <TabPane tab="Активные" key="active">
                          <ListView
                            vacancies={activeVacancies}
                            organisations={organisations}
                            visibleColumns={['id', 'job_title', 'organisation_id', 'department_id', 'status', 'candidate_status', 'completion_work', 'failure_reason', 'actions']}
                            isCandidateContext={true}
                            candidateId={candidate.id}
                            vacancyByCandidate={candidateVacancies}
                            funnelStatuses={funnelStatuses}
                            onStatusChange={handleStatusChange}
                            onReturnToWork={handleReturnToWork}
                            canChangeStatus={canChangeStatus}
                            canReturnToWork={canReturnToWork}
                          />
                        </TabPane>
                        <TabPane tab="Архивные" key="archived">
                          <ListView
                            vacancies={archivedVacancies}
                            organisations={organisations}
                            visibleColumns={['id', 'job_title', 'organisation_id', 'department_id', 'status', 'candidate_status', 'completion_work', 'result_candidate', 'actions']}
                            isCandidateContext={true}
                            candidateId={candidate.id}
                            vacancyByCandidate={candidateVacancies}
                            funnelStatuses={funnelStatuses}
                            onStatusChange={handleStatusChange}
                            onReturnToWork={handleReturnToWork}
                            canChangeStatus={canChangeStatus}
                            canReturnToWork={canReturnToWork}
                          />
                        </TabPane>
                      </Tabs>
                    );
                  })()}
                </div>
              ) : (
                <div>
                  <p>У кандидата нет связанных вакансий</p>
                </div>
              )}
            </TabPane>

            <TabPane tab="Задачи" key="tasks">
              {canCreateTask && (
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button type="primary" onClick={handleCreateTask}>
                    Создать задачу
                  </Button>
                </div>
              )}
              {candidateTasks.length === 0 ? <p>Нет задач у кандидата</p> :
                <TaskTable
                  tasks={candidateTasks}
                  candidates={[candidate]}
                  users={users}
                  visibleColumns={['id', 'description', 'vacancy_names', 'assignee_id', 'status', 'deadline', 'updated_at', 'actions']}
                  isModalMode={true}
                />}

              {isCreateTaskModalOpen && (
                <CreateModal
                  visible={isCreateTaskModalOpen}
                  onCancel={() => setIsCreateTaskModalOpen(false)}
                  onSubmit={handleSubmitTask}
                  vacancies={candidate?.vacancies || []}
                  users={users}
                  candidates={[candidate]}
                  initialValues={createTaskModalInitialValues}
                />
              )}
            </TabPane>

            <TabPane tab="История" key="history" loading={historyLoading}>
              {allHistoryItems.length === 0 ? <p>Нет истории</p> :
                <List
                  dataSource={allHistoryItems}
                  renderItem={item => renderHistoryItem(item, item.type)}
                />
              }
            </TabPane>

            <TabPane tab="Комментарии" key="comments" loading={historyLoading}>
              {comments && comments.length > 0 ? (
                <List
                  className="comment-list"
                  header={`${comments.length} комментария(ев)`}
                  itemLayout="horizontal"
                  dataSource={comments}
                  renderItem={item => <CustomComment item={item} users={users} />}
                />
              ) : (
                <p>Нет комментариев</p>
              )}
            </TabPane>
          </Tabs>

          {canComment && (
            <div style={{ marginTop: 24 }}>
              <label
                style={{
                  fontWeight: 'bold',
                  display: 'block',
                  marginBottom: 8,
                }}
              >
                Ваш комментарий
              </label>
              <TextArea
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Оставьте комментарий..."
              />
              <Button
                type="primary"
                onClick={handleCommentSubmit}
                style={{ marginTop: 12 }}
              >
                Отправить комментарий
              </Button>
            </div>
          )}
        </>
      )
      }

      <CompletionModal
        visible={completionModalVisible}
        onCancel={handleCompletionCancel}
        onConfirm={handleCompletionConfirm}
      />

      <Modal
        title="Возврат кандидата в работу"
        open={returnToWorkModalVisible}
        onOk={handleReturnToWorkConfirm}
        onCancel={handleReturnToWorkCancel}
        okText="Вернуть в работу"
        cancelText="Отмена"
        okButtonProps={{ disabled: !returnToWorkComment.trim() }}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Для возврата кандидата в работу необходимо указать комментарий:</p>
          <TextArea
            rows={4}
            placeholder="Введите комментарий (обязательно)"
            value={returnToWorkComment}
            onChange={(e) => setReturnToWorkComment(e.target.value)}
            required
          />
        </div>
      </Modal>

      <Modal
        title={`История изменений: ${selectedFieldHistory ?
          (selectedFieldHistory.fieldName === 'files' ?
            `Файл: ${selectedFieldHistory.fileName}` :
            getFieldDisplayName(selectedFieldHistory.fieldName)) : ''}`}
        visible={fieldHistoryModalVisible}
        onCancel={() => setFieldHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setFieldHistoryModalVisible(false)}>
            Закрыть
          </Button>
        ]}
        width={600}
      >
        {selectedFieldHistory && (
          <List
            dataSource={selectedFieldHistory.history}
            renderItem={(item, index) => {
              const user = users?.find(u => u.id === item.created_by);
              const authorName = user ? `${user.first_name} ${user.last_name}` : 'Неизвестный пользователь';

              if (selectedFieldHistory.fieldName === 'files') {

                let actionColor = '#52c41a';
                if (item.comment?.includes('Удален')) {
                  actionColor = '#ff4d4f';
                } else if (item.comment?.includes('Добавлен')) {
                  actionColor = '#52c41a';
                }

                return (
                  <List.Item>
                    <div style={{ width: '100%' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        {authorName}
                      </div>
                      <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: actionColor }}>
                          {item.comment || 'Действие с файлом'}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: '#999' }}>
                        {new Date(item.created_at).toLocaleString()}
                      </div>
                    </div>
                  </List.Item>
                );
              }

              const fieldHistory = selectedFieldHistory.history;
              let currentValue;

              if (index === fieldHistory.length - 1) {
                currentValue = candidate?.[selectedFieldHistory.fieldName];
              } else {
                currentValue = fieldHistory[index + 1].previous_value;
              }

              const isInitialSetup = item.previous_value === null || item.previous_value === 'None';

              return (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                      {authorName}
                    </div>
                    <div style={{ marginBottom: 4 }}>
                      {isInitialSetup ? (
                        <span style={{ color: '#52c41a' }}>
                          Установлено значение: "{currentValue || 'Не указано'}"
                        </span>
                      ) : (
                        <div>
                          <span style={{ color: '#999' }}>Было: </span>
                          <span style={{ textDecoration: 'line-through' }}>
                            {item.previous_value ? `"${item.previous_value}"` : 'Не указано'}
                          </span>
                          <span style={{ margin: '0 8px' }}>→</span>
                          <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                            {currentValue ? `"${currentValue}"` : 'Не указано'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#999' }}>
                      {new Date(item.created_at).toLocaleString()}
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Modal>
    </Modal >
  );
};

export default memo(CandidateViewModal);